import { SCORING, STRINGS } from '@/config/audio';
import type { ChordAnalysisResult, StringReport } from '@/types/audio';
import type { TranslationKey, TranslationParams } from '@/i18n';

export interface FeedbackItem {
  key: TranslationKey;
  params?: TranslationParams;
  tone: 'good' | 'warn' | 'bad';
}

/**
 * Turn an analysis result into at most two short sentences.
 *
 * Every branch here requires *evidence*. There is no fallback that invents a
 * plausible-sounding problem when the analyser has nothing to say — an empty
 * list is a valid, honest result.
 */
export function chordFeedback(result: ChordAnalysisResult, targetName: string): FeedbackItem[] {
  if (result.status === 'uncertain') {
    return [{ key: 'verdict.uncertainHint', tone: 'warn' }];
  }
  if (result.status === 'correct') return [];

  const items: FeedbackItem[] = [];
  const { purity, coverage, root, bass } = result.terms;

  // "You are playing a different chord" is only said when the blind detector is
  // confident *and* it disagrees with the target — otherwise it is a guess.
  if (result.detectedChord && result.detectedChord !== targetName && purity < 0.6) {
    items.push({
      key: 'feedback.soundsLike',
      params: { chord: result.detectedChord },
      tone: 'bad',
    });
  } else if (purity < 0.62) {
    items.push({ key: 'feedback.extraNotes', tone: 'bad' });
  }

  const notesMissing =
    result.noteCoverage !== undefined && result.noteCoverage < SCORING.noteCoverage.minForCorrect;
  if (coverage < 0.7 || notesMissing) {
    items.push({ key: 'feedback.missingNote', tone: 'warn' });
  }

  if (items.length < 2 && bass < 0.35) {
    items.push({ key: 'feedback.unwantedBass', tone: 'warn' });
  }

  if (items.length < 2 && root < 0.4) {
    items.push({ key: 'feedback.weakRoot', tone: 'warn' });
  }

  return items.slice(0, 2);
}

/**
 * Per-string issues, filtered by confidence and capped.
 *
 * Strings the analyser could not separate never appear here — see
 * `stringAnalysis.ts` for why some strings are unknowable in principle.
 */
export function stringFeedback(reports: StringReport[]): FeedbackItem[] {
  const issues = reports
    .filter((report) => report.state !== 'clear' && report.state !== 'indeterminate')
    .filter((report) => report.confidence >= STRINGS.reportConfidence)
    // Loudest problem first: an unwanted ringing string is more disruptive than
    // a slightly quiet one, and a dead string is worse than a weak one.
    .sort((a, b) => severity(b) - severity(a))
    .slice(0, STRINGS.maxReportedIssues);

  return issues.map((report) => ({
    key: keyForState(report.state),
    params: { string: report.string },
    tone: report.state === 'weak' ? ('warn' as const) : ('bad' as const),
  }));
}

/** Practical advice derived from the pattern of string problems. */
export function stringAdvice(reports: StringReport[]): FeedbackItem[] {
  const advice: FeedbackItem[] = [];
  const confident = reports.filter((r) => r.confidence >= STRINGS.reportConfidence);
  const dead = confident.filter((r) => r.state === 'silent' || r.state === 'weak');
  const unwanted = confident.filter((r) => r.state === 'unwanted');

  if (dead.length > 0) advice.push({ key: 'feedback.pressHarder', tone: 'warn' });
  if (unwanted.length > 0) advice.push({ key: 'feedback.avoidTouching', tone: 'warn' });
  return advice.slice(0, 2);
}

function severity(report: StringReport): number {
  switch (report.state) {
    case 'unwanted':
      return 3;
    case 'silent':
      return 2;
    case 'weak':
      return 1;
    default:
      return 0;
  }
}

function keyForState(state: StringReport['state']): TranslationKey {
  switch (state) {
    case 'weak':
      return 'feedback.stringWeak';
    case 'silent':
      return 'feedback.stringSilent';
    case 'unwanted':
      return 'feedback.stringUnwanted';
    default:
      return 'feedback.stringClear';
  }
}
